/**
 * State a message: what it carries, and how often (issue #52).
 *
 * A message is the payload a software ICD lists - its FIELDS, each with a type
 * and a unit, and its rate. Those are the questions asked, rather than a type
 * box: every message is a `Message`, and what makes one useful is what is in it.
 *
 * The rate is left blank for an event-driven message, which is the library's own
 * rule: `publishRate` is `[0..1]` because only periodic messages have one.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { AttributeTypeChoice } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import NewAttributesTable from '../parts/NewAttributesTable'
import { attributePayload, emptyAttributeDraft } from '../parts/part-wizard-model'
import type { AttributeDraft } from '../parts/part-wizard-model'

export default function MessageWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const toasts = useToasts()
  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [rate, setRate] = useState('')
  const [statement, setStatement] = useState('')
  const [fields, setFields] = useState<AttributeDraft[]>([emptyAttributeDraft('field-0')])
  const [attributeTypes, setAttributeTypes] = useState<AttributeTypeChoice[]>([])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    api.attributeTypes()
      .then((answer) => live && setAttributeTypes(answer))
      .catch(() => live && setAttributeTypes([]))
    return () => {
      live = false
    }
  }, [])

  const submit = async () => {
    setBusy(true)
    setFailure('')
    try {
      const made = await api.createMessage({
        name: name.trim(),
        identifier: identifier.trim() || null,
        publish_rate: rate.trim(),
        fields: attributePayload(fields),
        statement,
      })
      toasts.succeeded(`${made.identifier || made.name} created`)
      onCreated(made.identifier || made.name)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>New message</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              helperText="e.g. HousekeepingTelemetry"
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
            <TextField
              label="ID"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              size="small"
              helperText="Optional, e.g. MSG_HK"
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
          </Stack>
          <TextField
            label="Publish rate"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            size="small"
            helperText="How often it is sent. Leave empty for an event-driven message."
            slotProps={{ input: { endAdornment: <InputAdornment position="end">Hz</InputAdornment> } }}
          />
          <Box>
            <Typography variant="subtitle2">Fields</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              What the message carries - a temperature, a bus voltage, a mode. Each field&apos;s type
              gives its unit, which is what the ICD prints beside it.
            </Typography>
            <NewAttributesTable
              rows={fields}
              choices={attributeTypes}
              onChange={setFields}
              disabled={busy}
              showRollsUp={false}
            />
          </Box>
          <TextField
            label="Description"
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            multiline
            minRows={2}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!name.trim() || busy}>
          {busy ? 'Creating…' : 'Create message'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
